import { useState, useEffect, createContext, useContext } from "react";
import { useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { API } from "../config";

const AuthContext = createContext(null);

// Named export so components that want to opt-out of the throwing
// ``useAuth()`` hook (e.g. widgets mounted in dual-context navbars
// like IT Portal + Admin Dashboard) can read the context safely with
// ``useContext(AuthContext)`` and get ``null`` when not wrapped.
export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('lojack_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem('lojack_token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  // Auto-attach admin token to all axios requests + handle 401 (session expired/kicked)
  useEffect(() => {
    const reqInt = axios.interceptors.request.use((config) => {
      // 24/08/2026 — P0 emergency auth wall fallback: some pages of
      // the app (notably ``ITPortalPage.js``) authenticate via a
      // separate localStorage key (``it_token``) and their fetch
      // helpers do NOT set an explicit ``Authorization`` header —
      // they used to rely on the endpoints being publicly reachable.
      // The wall now 401-blocks those anonymous calls, so this
      // interceptor tries ``lojack_token`` first and falls back to
      // ``it_token`` when the former is absent, so both admin and
      // IT logins keep working without touching each caller.
      const t = localStorage.getItem('lojack_token') || localStorage.getItem('it_token');
      if (t && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });
    const respInt = axios.interceptors.response.use(
      (resp) => resp,
      (err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || "";
        const wasAuthed = !!localStorage.getItem('lojack_token');
        if (status === 401 && wasAuthed) {
          const isSessionKick = typeof detail === "string" && detail.includes("altro dispositivo");
          localStorage.removeItem('lojack_token');
          setToken(null);
          setUser(null);
          if (isSessionKick) {
            try {
              window.sessionStorage.setItem(
                "lojack_logout_reason",
                "Sessione terminata: l'account è stato aperto da un altro dispositivo.",
              );
            } catch {/* ignore */}
          }
          if (!window.location.pathname.startsWith("/login")) {
            window.location.href = "/login";
          }
        }
        return Promise.reject(err);
      },
    );
    return () => {
      axios.interceptors.request.eject(reqInt);
      axios.interceptors.response.eject(respInt);
    };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // If the admin has 2FA active, the backend returns a short-lived
    // mfa_challenge_token instead of the session JWT. The caller must
    // collect the 6-digit code and call `completeMfaLogin()` to swap
    // the challenge token for the real session.
    if (response.data?.mfa_required) {
      return { mfa_required: true,
               mfa_method: response.data.mfa_method,
               mfa_challenge_token: response.data.mfa_challenge_token };
    }
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('lojack_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const completeMfaLogin = (loginPayload) => {
    // Called by <MFAChallengeModal /> on successful /api/mfa/verify.
    // The backend replays the original login payload (token + user).
    const { token: newToken, user: userData } = loginPayload || {};
    if (!newToken || !userData) return;
    localStorage.setItem('lojack_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('lojack_token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, getAuthHeader, completeMfaLogin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-lojack-black">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Fabrizio HOTFIX 26/02/2026 — role="salesforce" (Motta) has no business
  // on /admin/*: the widgets there call admin-only APIs that 403 for
  // her, producing phantom red toasts (es. "Errore nel caricamento dei
  // mismatch"). Bounce her to her dedicated portal instead.
  if (user.role === "salesforce" && location.pathname.startsWith("/admin")) {
    return <Navigate to="/salesforce" replace />;
  }

  return children;
};
